import type { AdminMailEmailPublic } from '@accounts/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { type CSSProperties, useState } from 'react';
import { Link } from 'react-router-dom';

import { STR } from '../../../../i18n';
import { listAdminMail } from '../../../../lib/api';
import { formatDateTime, relativeTime } from '../../../../lib/format';
import { queryKeys } from '../../../../lib/queryKeys';
import { surfaceById } from '../../../../lib/surfaces';
import { Callout, Card, Chip, EmptyRow, Skeleton } from '../../../../ui/Primitives';
import { monoStyle, quietButtonStyle, rowActionStyle } from '../../../../ui/styles';
import { ANSWER_EMAIL_STATUS_TONE } from '../accessFormat';

/** One page of the outbox (the server's default page size). */
const PAGE_SIZE = 50;

const rowGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px,1fr) minmax(180px,1.6fr) minmax(120px,.8fr) 96px 120px auto',
  gap: 12,
  alignItems: 'center',
  padding: '9px 16px',
  fontSize: 12.5,
};

const ellipsis: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** The Queue address the bell uses for the same request (SIR-7690's link convention). */
function requestLink(email: AdminMailEmailPublic): string {
  const name = email.related.type === 'access_request' ? 'requestId' : 'otherRequestId';
  return `${surfaceById('accessQueue').path}?${name}=${encodeURIComponent(email.related.id)}`;
}

/**
 * EMAILS (board mail decision, 19 Oct; SIR-7687 c2b7ba01): every answer email Accounts
 * writes to the person who asked, newest first, one page at a time. Administrators, HR and
 * IT read it (SIR-7864); nobody changes anything here. Anyone else meets the route guard here
 * and a 403 from the server.
 *
 * Under the default `capture` driver nothing leaves the server: the outbox row IS the email,
 * and this page is where it can be read. Each row carries the driver that handled it (the
 * list reports no current driver of its own), so the banner says so plainly whenever a row
 * on the page shown was captured. Each row opens to its full text.
 */
export function MailPanel() {
  const [pageNo, setPageNo] = useState(1);
  const [open, setOpen] = useState<string | null>(null);
  const page = { page: pageNo, pageSize: PAGE_SIZE };

  const mail = useQuery({
    queryKey: queryKeys.adminMail(page),
    queryFn: () => listAdminMail(page),
    placeholderData: keepPreviousData,
  });

  if (mail.isLoading) {
    return (
      <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Skeleton style={{ height: 40, borderRadius: 9 }} />
        <Skeleton style={{ height: 320, borderRadius: 12 }} />
      </div>
    );
  }

  if (mail.isError || mail.data === undefined) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Callout
          tone="danger"
          role="alert"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <span style={{ flex: 1, minWidth: 180 }}>{STR.errors.couldNotLoad}</span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            onClick={() => {
              void mail.refetch();
            }}
          >
            {STR.common.tryAgain}
          </button>
        </Callout>
      </div>
    );
  }

  const { data: rows, meta } = mail.data;
  const offset = (meta.page - 1) * meta.pageSize;
  const from = meta.total === 0 ? 0 : offset + 1;
  const to = offset + rows.length;
  const captured = rows.some((email) => email.driver === 'capture');

  return (
    <div style={{ padding: '20px 24px 110px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      {captured ? (
        <Callout tone="attention" role="status">
          <b>{STR.answerEmails.captureBanner}</b> {STR.answerEmails.captureMore}
        </Callout>
      ) : null}

      <Card>
        <div
          className="tx-subtle"
          style={{
            ...rowGrid,
            borderBottom: '1px solid var(--border)',
            background: 'var(--surface-2)',
            fontSize: 11.5,
            fontWeight: 650,
          }}
        >
          <span>{STR.answerEmails.columns.to}</span>
          <span>{STR.answerEmails.columns.subject}</span>
          <span>{STR.answerEmails.columns.kind}</span>
          <span>{STR.answerEmails.columns.status}</span>
          <span>{STR.answerEmails.columns.at}</span>
          <span />
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {rows.length === 0 ? (
            <li>
              <EmptyRow>{STR.answerEmails.empty}</EmptyRow>
            </li>
          ) : null}
          {rows.map((email) => {
            const isOpen = open === email.id;
            return (
              <li key={email.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <div className="row" style={rowGrid}>
                  <span style={{ ...ellipsis, ...monoStyle, fontSize: 12 }} title={email.to}>
                    {email.to}
                  </span>
                  <button
                    type="button"
                    className="tbtn"
                    aria-expanded={isOpen}
                    onClick={() => {
                      setOpen(isOpen ? null : email.id);
                    }}
                    style={{
                      ...ellipsis,
                      padding: 0,
                      border: 'none',
                      textAlign: 'left',
                      fontSize: 12.5,
                      fontWeight: 650,
                      color: 'var(--text)',
                    }}
                    title={email.subject}
                  >
                    {email.subject}
                  </button>
                  <span className="tx-subtle" style={ellipsis}>
                    {STR.answerEmails.kind[email.kind]}
                  </span>
                  <span>
                    <Chip tone={ANSWER_EMAIL_STATUS_TONE[email.status]} size="sm">
                      {STR.answerEmails.status[email.status]}
                    </Chip>
                  </span>
                  <span
                    className="tx-subtle"
                    title={formatDateTime(email.createdAt)}
                    style={{ fontVariantNumeric: 'tabular-nums', fontSize: 12 }}
                  >
                    {relativeTime(email.createdAt)}
                  </span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Link to={requestLink(email)} className="tbtn" style={{ ...rowActionStyle, textDecoration: 'none' }}>
                      {STR.answerEmails.openRequest}
                    </Link>
                  </span>
                </div>
                {isOpen ? (
                  <pre
                    style={{
                      margin: '0 16px 12px',
                      padding: '10px 12px',
                      borderRadius: 10,
                      border: '1px solid var(--border)',
                      background: 'var(--surface-2)',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {email.body}
                  </pre>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Card>

      {meta.total > meta.pageSize ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            disabled={meta.page <= 1}
            onClick={() => {
              setPageNo(Math.max(1, meta.page - 1));
            }}
          >
            {STR.answerEmails.newer}
          </button>
          <span className="tx-subtle" style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
            {STR.answerEmails.range(from, to, meta.total)}
          </span>
          <button
            type="button"
            className="tbtn"
            style={quietButtonStyle}
            disabled={to >= meta.total}
            onClick={() => {
              setPageNo(meta.page + 1);
            }}
          >
            {STR.answerEmails.older}
          </button>
        </div>
      ) : null}
    </div>
  );
}
